import { useMemo, useState } from "preact/hooks";
import { navigate } from "../app/router";

type Kind = "root" | "disc" | "area" | "out";
type Node = { id: string; label: string; x: number; y: number; r: number; kind: Kind; href?: string };

const NODES: Node[] = [
  { id: "ns", label: "Natural Sciences", x: 78, y: 210, r: 7, kind: "root", href: "/radar" },
  { id: "bio", label: "Biology", x: 210, y: 108, r: 6, kind: "disc", href: "/radar?discipline=biology&type=skill" },
  { id: "chem", label: "Chemistry", x: 210, y: 312, r: 6, kind: "disc", href: "/radar?discipline=chemistry&type=skill" },
  { id: "bioinfo", label: "Bioinformatics", x: 368, y: 48, r: 5, kind: "area", href: "/radar?type=technology&entity=tech-bioinformatics" },
  { id: "biotech", label: "Biotechnology", x: 388, y: 118, r: 5, kind: "area", href: "/radar?type=technology&entity=tech-biotechnology" },
  { id: "data", label: "Scientific Data", x: 368, y: 188, r: 5, kind: "area", href: "/radar?type=technology&entity=tech-scientific-data-analytics" },
  { id: "analytical", label: "Analytical Science", x: 388, y: 258, r: 5, kind: "area", href: "/radar?type=technology&entity=tech-advanced-analytical" },
  { id: "green", label: "Green Chemistry", x: 368, y: 328, r: 5, kind: "area", href: "/radar?type=technology&entity=tech-green-chemistry" },
  { id: "drug", label: "Drug Discovery", x: 388, y: 392, r: 5, kind: "area", href: "/radar?type=technology&entity=tech-drug-discovery" },
  { id: "skills", label: "Skills", x: 548, y: 150, r: 6, kind: "out", href: "/radar?type=skill" },
  { id: "careers", label: "Career Pathways", x: 560, y: 270, r: 6, kind: "out", href: "/radar?type=career&group=all" },
];

const LINKS: [string, string][] = [
  ["ns", "bio"],
  ["ns", "chem"],
  ["bio", "bioinfo"],
  ["bio", "biotech"],
  ["bio", "data"],
  ["chem", "analytical"],
  ["chem", "green"],
  ["chem", "drug"],
  ["data", "analytical"],
  ["bioinfo", "skills"],
  ["biotech", "careers"],
  ["data", "skills"],
  ["analytical", "careers"],
  ["green", "skills"],
  ["drug", "careers"],
  ["skills", "careers"],
];

const DEFAULT_PATH = ["ns", "bio", "bioinfo", "skills"];

function neighbors(id: string): Set<string> {
  const next = new Set<string>([id]);
  for (const [a, b] of LINKS) {
    if (a === id) next.add(b);
    if (b === id) next.add(a);
  }
  return next;
}

export function HeroNetwork() {
  const [active, setActive] = useState<string | undefined>();
  const byId = useMemo(() => new Map(NODES.map((n) => [n.id, n])), []);
  const hotNodes = active ? neighbors(active) : new Set(DEFAULT_PATH);
  const hotLinks = new Set(
    LINKS.filter(([a, b]) => hotNodes.has(a) && hotNodes.has(b)).map(([a, b]) => `${a}|${b}`),
  );

  return (
    <div class="obs-hero-net">
      <svg
        class="obs-hero-net-svg"
        viewBox="0 0 640 440"
        role="img"
        aria-label="Knowledge network connecting Natural Sciences, Biology, Chemistry, skills and career pathways"
      >
        <defs>
          <pattern id="obs-hero-grid" width="28" height="28" patternUnits="userSpaceOnUse">
            <path d="M28 0H0V28" fill="none" stroke="#d7e0ea" stroke-width="0.55" />
          </pattern>
        </defs>
        <rect width="640" height="440" fill="url(#obs-hero-grid)" opacity="0.45" />
        {LINKS.map(([a, b]) => {
          const from = byId.get(a);
          const to = byId.get(b);
          if (!from || !to) return null;
          const hot = hotLinks.has(`${a}|${b}`);
          return (
            <line
              key={`${a}-${b}`}
              class={hot ? "obs-hero-link is-path" : "obs-hero-link"}
              x1={from.x}
              y1={from.y}
              x2={to.x}
              y2={to.y}
            />
          );
        })}
        {NODES.map((n) => {
          const hot = hotNodes.has(n.id);
          const labelBelow = n.y < 70;
          return (
            <g
              key={n.id}
              class={`obs-hero-node obs-hero-node--${n.kind}${hot ? " is-path" : ""}`}
              transform={`translate(${n.x} ${n.y})`}
              tabIndex={0}
              role="button"
              aria-pressed={active === n.id}
              aria-label={n.label}
              onMouseEnter={() => setActive(n.id)}
              onMouseLeave={() => setActive(undefined)}
              onFocus={() => setActive(n.id)}
              onBlur={() => setActive((id) => (id === n.id ? undefined : id))}
              onClick={() => {
                setActive(n.id);
                if (!n.href) return;
                const [path, query] = n.href.split("?");
                navigate(path, query ? `?${query}` : "");
              }}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  setActive(n.id);
                  if (!n.href) return;
                  const [path, query] = n.href.split("?");
                  navigate(path, query ? `?${query}` : "");
                }
              }}
            >
              <circle r={n.r} />
              <text y={labelBelow ? 18 : -12} text-anchor="middle">
                {n.label}
              </text>
            </g>
          );
        })}
      </svg>
    </div>
  );
}
